import type { DatabaseDriver } from '@w/db'
import { identifier, keyword, tokenize } from './lexer'
import { fold, quoteIdent } from './names'
import type { Statement } from './expand'

/** INV-index: local indexes cannot change expansion success (I9).
 * Contract index rule (a)-(d): save/drop touched-table and touched-name indexes,
 * expand without them, then restore every saved CREATE INDEX IF NOT EXISTS.
 * Index SQL never enters expanded or the DDL log (I13).
 */
export async function withLocalIndexes<T>(
  tx: Pick<DatabaseDriver, 'query' | 'exec' | 'transaction'>,
  expanded: readonly Statement[],
  run: () => Promise<T>,
): Promise<T> {
  const tables = new Set<string>()
  const names = new Set<string>()
  for (const statement of expanded) {
    const tokens = tokenize(statement.sql)
    if (!['CREATE', 'ALTER', 'DROP'].some(word => keyword(tokens[0], word))) continue
    let at = keyword(tokens[2], 'IF') ? (keyword(tokens[3], 'NOT') ? 5 : 4) : 2
    if (tokens[at + 1]?.raw === '.') at += 2
    const target = tokens[at]?.value
    if (!target) throw new Error('Invalid expansion target')
    if (keyword(tokens[1], 'TABLE')) tables.add(fold(target))
    if (keyword(tokens[0], 'CREATE')) names.add(fold(target))
    if (keyword(tokens[0], 'ALTER') && keyword(tokens[at + 1], 'RENAME') && keyword(tokens[at + 2], 'TO')) names.add(fold(tokens[at + 3].value))
  }
  const local = await tx.query<{ name: string; tbl_name: string; sql: string }>("SELECT name, tbl_name, sql FROM sqlite_schema WHERE type='index' AND sql IS NOT NULL")
  const indexes = local.filter(index => tables.has(fold(index.tbl_name)) || names.has(fold(index.name)))
  for (const index of indexes) await tx.exec(`DROP INDEX ${quoteIdent(index.name)}`)
  const result = await run()
  for (const index of indexes) {
    const tokens = tokenize(index.sql)
    const at = tokens.findIndex(token => keyword(token, 'INDEX'))
    if (at < 0) throw new Error('Invalid stored CREATE INDEX')
    const sql = keyword(tokens[at + 1], 'IF') ? index.sql : `${index.sql.slice(0, tokens[at].end)} IF NOT EXISTS${index.sql.slice(tokens[at].end)}`
    // A failed CREATE INDEX can leave the engine's schema invalidated until
    // rollback. Its own savepoint keeps restoration outside the DDL verdict.
    // Without this savepoint, restoration caused "Database schema changed".
    try { await tx.transaction(nested => nested.exec(sql)) }
    catch (error) {
      // Restoration cannot veto DDL: an index that no longer fits stays absent.
      // Unknown errors, including I/O/busy/corruption, still propagate for retry.
      // Turso LimboError exposes semantic ParseError/ConversionError prefixes;
      // I/O, Busy, Corrupt and internal errors are separate variants.
      const obsolete = /(?:Parse|Conversion) error:|no such (column|table)|column .+ (does not exist|not found)|unknown column|invalid expression in CREATE INDEX|already (?:a table|an index|exists)|constraint (?:failed|violation)|integer overflow|malformed JSON/iu.test(String(error))
      if (!obsolete) throw error
    }
  }
  for (const statement of expanded) {
    const tokens = tokenize(statement.sql)
    if (!keyword(tokens[0], 'ALTER') || !keyword(tokens[1], 'TABLE')) continue
    const add = tokens.findIndex(token => keyword(token, 'ADD'))
    if (add < 0 || !tokens.some(token => keyword(token, 'REFERENCES'))) continue
    const column = keyword(tokens[add + 1], 'COLUMN') ? tokens[add + 2] : tokens[add + 1]
    // Expanded ADD COLUMN uses the user's actual target, including quoted identifiers.
    const target = tokens[3]?.value === '.' ? tokens[4] : tokens[2]
    if (!column || !identifier(column) || !target) throw new Error('Invalid ADD COLUMN relation')
    await tx.exec(`CREATE INDEX IF NOT EXISTS ${quoteIdent(`${target.value}__rel_${column.value}`)} ON ${quoteIdent(target.value)}(${quoteIdent(column.value)})`)
  }
  return result
}
